import * as Android from '@/api/android';
import LimitMenuResponse from '@/api/android/interfaces/LimitMenuResponse';
import * as Server from '@/api/server';
import StaffCall from '@/components/StaffCall.vue';
import { logger } from '@/logger';
import { useCommonStore } from '@/stores/common';
import { useConfigStore } from '@/stores/config';
import { useDeviceStore } from '@/stores/device';
import { useMenuStore } from '@/stores/menu';
import { useModalStore } from '@/stores/modal';
import { wait } from '@/utils';

declare global {
  interface Window {
    hideBanner: () => void;
    setPaymentMode: (isPaymentModeUsed: boolean) => void;
    getOrderLimitItems: (response: LimitMenuResponse) => void;
    openStaffCallPage: () => void;
    callRobot: () => void;
    getTableName: () => string;
    refreshDeviceStatus: () => void;
  }
}

export const iniAndroidCallbacks = () => {
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const menuStore = useMenuStore();
  const deviceStore = useDeviceStore();

  /** 광고 숨김 콜백 함수 (POS에서 호출) */
  window.hideBanner = async () => {
    logger.info('[Callback] onHideBanner');
    const loadingId = commonStore.showLoading();

    if (configStore.config.IsFastOrderUsed) {
      await menuStore.setData(true);
    } else {
      // 실시간 데이터 갱신
      await commonStore.fetchRealtimeData(true);
    }

    // 대기 화면 팝업 타임아웃 설정
    commonStore.setWaitingPopupInterval();
    commonStore.hideLoading(loadingId);
  };

  /**
   * 결제모드 사용여부 옵션 변경 콜백 함수 (POS에서 호출)
   * @param isPaymentModeUsed 결제모드 사용여부
   */
  window.setPaymentMode = async (isPaymentModeUsed: boolean) => {
    logger.info('[Callback] onSetPaymentMode', isPaymentModeUsed);
    // 현재 로컬 설정과 변경된 설정이 다른지 비교
    let isDiff = true;
    if (configStore.localConfig.IsPaymentModeUsed === undefined && isPaymentModeUsed === true) isDiff = false;
    if (configStore.localConfig.IsPaymentModeUsed === false && isPaymentModeUsed === false) isDiff = false;

    if (isDiff) {
      const localConfig = configStore.localConfig;
      localConfig.IsPaymentModeUsed = isPaymentModeUsed ? undefined : false;
      configStore.setLocalConfig(localConfig);

      // 변경된 옵션 저장
      Android.saveOption();

      // 앱을 재시작하기 전에 로컬스토리지 안전한 저장을 위해 5초 대기
      await wait(5000);

      // 앱 재시작
      Android.reStart();
    }
  };

  /**
   * 한정수량 조회 콜백 함수
   * @param response 한정수량 데이터
   */
  window.getOrderLimitItems = (response: LimitMenuResponse) => {
    logger.info('[Callback] onGetOrderLimitItems', response);
    if (response.RET_CODE !== 0) return;
    menuStore.setLimitMenus(response.DATA);
  };

  /** 직원 호출 페이지 열기 콜백 함수 */
  window.openStaffCallPage = () => {
    logger.info('[Callback] openStaffCallPage');
    modalStore.openFullScreenModal(StaffCall, {});
  };

  /** 로봇 호출 콜백 함수 */
  window.callRobot = async () => {
    logger.info('[Callback] callRobot');
    if (!configStore.config.TableCode) return;
    const response = await Server.callKeenonRobot(configStore.config.TableCode);
    Android.completeRobot(response.RET_CODE.toString());
  };

  /** 테이블 이름 조회 콜백 함수 */
  window.getTableName = () => {
    logger.info('[Callback] getTableName');
    return configStore.config.TableName || '';
  };

  /** 디바이스 상태 설정 콜백 함수 */
  window.refreshDeviceStatus = () => {
    logger.info('[Callback] refreshDeviceStatus');
    deviceStore.refreshStatus();
  };
};
